import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { 
  ClipboardCheck, Save, X, Loader2, ChevronDown, ChevronUp,
  Activity, Dumbbell, Zap, AlertCircle, CheckCircle, TrendingUp
} from 'lucide-react';
import { toast } from 'sonner';
import { API } from '../config/api';

export default function PlayerProfiling({ childId, childName, onClose, onProfileSaved }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [assessmentCategories, setAssessmentCategories] = useState(null);
  const [latestProfile, setLatestProfile] = useState(null);
  const [expandedSections, setExpandedSections] = useState({
    biometrics: true,
    movement: true,
    strength: false,
    performance: false
  });
  
  const [profileData, setProfileData] = useState({
    assessment_date: new Date().toISOString().split('T')[0],
    biometrics: {
      standing_height_cm: '',
      sitting_height_cm: '',
      weight_kg: '',
      resting_heart_rate: ''
    },
    assessments: {},
    overall_notes: ''
  });

  useEffect(() => {
    fetchAssessmentCategories();
    fetchLatestProfile();
  }, [childId]);

  const fetchAssessmentCategories = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/profiling/assessments`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAssessmentCategories(response.data);
      
      // Initialize assessments object
      const initialAssessments = {};
      [...response.data.movement, ...response.data.strength, ...response.data.performance].forEach(a => {
        initialAssessments[a.id] = { rating: null, measured_value: '', comments: '' };
      });
      setProfileData(prev => ({ ...prev, assessments: initialAssessments }));
    } catch (err) {
      console.error('Failed to fetch assessment categories:', err);
      toast.error('Failed to load assessment categories');
    } finally {
      setLoading(false);
    }
  };

  const fetchLatestProfile = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/profiling/athlete/${childId}/latest-profile`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.profile) {
        setLatestProfile(response.data.profile);
      }
    } catch (err) {
      console.error('Failed to fetch latest profile:', err);
    }
  };

  const handleRatingChange = (assessmentId, rating) => {
    setProfileData(prev => ({
      ...prev,
      assessments: {
        ...prev.assessments,
        [assessmentId]: {
          ...prev.assessments[assessmentId],
          rating: rating
        }
      }
    }));
  };

  const handleMeasuredValueChange = (assessmentId, value) => {
    setProfileData(prev => ({
      ...prev,
      assessments: {
        ...prev.assessments,
        [assessmentId]: {
          ...prev.assessments[assessmentId],
          measured_value: value
        }
      }
    }));
  };

  const handleCommentChange = (assessmentId, comment) => {
    setProfileData(prev => ({
      ...prev,
      assessments: {
        ...prev.assessments,
        [assessmentId]: {
          ...prev.assessments[assessmentId],
          comments: comment
        }
      }
    }));
  };

  const saveProfile = async () => {
    // Validate that at least some assessments are filled
    const filledAssessments = Object.values(profileData.assessments).filter(a => a.rating !== null);
    if (filledAssessments.length === 0) {
      toast.error('Please complete at least one assessment');
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/profiling/athlete/${childId}/profile`, profileData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Profile saved! Overall score: ${response.data.overall_score}/5`);
      if (onProfileSaved) onProfileSaved(response.data);
      if (onClose) onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save profile');
    } finally {
      setSaving(false);
    }
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const getRatingColor = (rating) => {
    const colors = {
      1: 'bg-red-500',
      2: 'bg-orange-500',
      3: 'bg-yellow-500',
      4: 'bg-green-500',
      5: 'bg-emerald-500'
    };
    return colors[rating] || 'bg-zinc-700';
  };

  const getRatingLabel = (rating) => {
    const labels = {
      1: 'Poor',
      2: 'Below Avg',
      3: 'Average',
      4: 'Good',
      5: 'Excellent'
    };
    return labels[rating] || '';
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-[#02b5ff]" />
      </div>
    );
  }

  const renderAssessmentSection = (title, icon, assessments, sectionKey, color) => (
    <div className="border border-zinc-700/50 rounded-xl overflow-hidden">
      <button
        onClick={() => toggleSection(sectionKey)}
        className={`w-full flex items-center justify-between p-4 ${color} transition-all`}
      >
        <div className="flex items-center gap-3">
          {icon}
          <span className="font-semibold text-white">{title}</span>
          <span className="text-xs text-white/70">({assessments.length} tests)</span>
        </div>
        {expandedSections[sectionKey] ? <ChevronUp className="h-5 w-5 text-white" /> : <ChevronDown className="h-5 w-5 text-white" />}
      </button>
      
      {expandedSections[sectionKey] && (
        <div className="p-4 space-y-4 bg-zinc-900/50">
          {assessments.map(assessment => {
            const currentValue = profileData.assessments[assessment.id] || {};
            const previousValue = latestProfile?.assessments?.[assessment.id];
            
            return (
              <div key={assessment.id} className="bg-zinc-800/50 rounded-lg p-4">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <p className="font-medium text-white">{assessment.name}</p>
                    <p className="text-xs text-zinc-500">{assessment.description}</p>
                  </div>
                  {previousValue?.rating && (
                    <div className="text-xs text-zinc-400 flex items-center gap-1">
                      <TrendingUp className="h-3 w-3" />
                      Previous: {previousValue.rating}/5
                    </div>
                  )}
                </div>
                
                {/* Rating Buttons */}
                <div className="flex gap-2 mb-3">
                  {[1, 2, 3, 4, 5].map(rating => (
                    <button
                      key={rating}
                      onClick={() => handleRatingChange(assessment.id, rating)}
                      className={`flex-1 py-2 rounded-lg text-sm font-medium transition-all ${
                        currentValue.rating === rating
                          ? `${getRatingColor(rating)} text-white`
                          : 'bg-zinc-700 text-zinc-400 hover:bg-zinc-600'
                      }`}
                    >
                      {rating}
                    </button>
                  ))}
                </div>
                <p className="text-xs text-center text-zinc-500 mb-3">
                  {currentValue.rating ? getRatingLabel(currentValue.rating) : 'Select rating (1-5)'}
                </p>
                
                {/* Measured Value (optional) */}
                {assessment.unit && (
                  <div className="flex gap-2 mb-2">
                    <Input
                      type="number"
                      placeholder={`Measured value (${assessment.unit})`}
                      value={currentValue.measured_value || ''}
                      onChange={(e) => handleMeasuredValueChange(assessment.id, e.target.value)}
                      className="bg-zinc-800 border-zinc-700 text-white text-sm"
                    />
                  </div>
                )}
                
                {/* Comments */}
                <Input
                  placeholder="Comments (optional)"
                  value={currentValue.comments || ''}
                  onChange={(e) => handleCommentChange(assessment.id, e.target.value)}
                  className="bg-zinc-800 border-zinc-700 text-white text-sm"
                />
              </div>
            );
          })}
        </div>
      )}
    </div>
  );

  return (
    <div className="fixed inset-0 bg-black/90 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div 
        className="bg-zinc-900 border border-zinc-700 rounded-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-zinc-700">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-gradient-to-br from-[#02b5ff] to-[#ff025f] rounded-xl flex items-center justify-center">
              <ClipboardCheck className="h-6 w-6 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">Player Profiling Assessment</h2>
              <p className="text-sm text-zinc-400">{childName}</p>
            </div>
          </div>
          <button onClick={onClose} className="text-zinc-500 hover:text-white p-2">
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Assessment Date */}
          <div className="bg-zinc-800/50 rounded-xl p-4">
            <label className="text-sm text-zinc-400 mb-2 block">Assessment Date</label>
            <Input
              type="date"
              value={profileData.assessment_date}
              onChange={(e) => setProfileData(prev => ({ ...prev, assessment_date: e.target.value }))}
              className="bg-zinc-800 border-zinc-700 text-white w-48"
            />
          </div>

          {/* Biometrics Section */}
          <div className="border border-zinc-700/50 rounded-xl overflow-hidden">
            <button
              onClick={() => toggleSection('biometrics')}
              className="w-full flex items-center justify-between p-4 bg-gradient-to-r from-cyan-500/20 to-blue-500/20"
            >
              <div className="flex items-center gap-3">
                <Activity className="h-5 w-5 text-cyan-400" />
                <span className="font-semibold text-white">Biometrics</span>
              </div>
              {expandedSections.biometrics ? <ChevronUp className="h-5 w-5 text-white" /> : <ChevronDown className="h-5 w-5 text-white" />}
            </button>
            
            {expandedSections.biometrics && (
              <div className="p-4 grid grid-cols-2 md:grid-cols-4 gap-4 bg-zinc-900/50">
                <div>
                  <label className="text-xs text-zinc-400 mb-1 block">Standing Height (cm)</label>
                  <Input
                    type="number"
                    step="0.1"
                    value={profileData.biometrics.standing_height_cm}
                    onChange={(e) => setProfileData(prev => ({
                      ...prev,
                      biometrics: { ...prev.biometrics, standing_height_cm: e.target.value }
                    }))}
                    className="bg-zinc-800 border-zinc-700 text-white"
                  />
                </div>
                <div>
                  <label className="text-xs text-zinc-400 mb-1 block">Sitting Height (cm)</label>
                  <Input
                    type="number"
                    step="0.1"
                    value={profileData.biometrics.sitting_height_cm}
                    onChange={(e) => setProfileData(prev => ({
                      ...prev,
                      biometrics: { ...prev.biometrics, sitting_height_cm: e.target.value }
                    }))}
                    className="bg-zinc-800 border-zinc-700 text-white"
                  />
                </div>
                <div>
                  <label className="text-xs text-zinc-400 mb-1 block">Weight (kg)</label>
                  <Input
                    type="number"
                    step="0.1"
                    value={profileData.biometrics.weight_kg}
                    onChange={(e) => setProfileData(prev => ({
                      ...prev,
                      biometrics: { ...prev.biometrics, weight_kg: e.target.value }
                    }))}
                    className="bg-zinc-800 border-zinc-700 text-white"
                  />
                </div>
                <div>
                  <label className="text-xs text-zinc-400 mb-1 block">Resting HR (bpm)</label>
                  <Input
                    type="number"
                    value={profileData.biometrics.resting_heart_rate}
                    onChange={(e) => setProfileData(prev => ({
                      ...prev,
                      biometrics: { ...prev.biometrics, resting_heart_rate: e.target.value }
                    }))}
                    className="bg-zinc-800 border-zinc-700 text-white"
                  />
                </div>
              </div>
            )}
          </div>

          {/* Movement Assessments */}
          {assessmentCategories && renderAssessmentSection(
            'Movement Assessments',
            <Activity className="h-5 w-5 text-blue-400" />,
            assessmentCategories.movement,
            'movement',
            'bg-gradient-to-r from-blue-500/20 to-indigo-500/20'
          )}

          {/* Strength Assessments */}
          {assessmentCategories && renderAssessmentSection(
            'Strength Assessments',
            <Dumbbell className="h-5 w-5 text-orange-400" />,
            assessmentCategories.strength,
            'strength',
            'bg-gradient-to-r from-orange-500/20 to-red-500/20'
          )}

          {/* Performance Assessments */}
          {assessmentCategories && renderAssessmentSection(
            'Performance Assessments',
            <Zap className="h-5 w-5 text-yellow-400" />,
            assessmentCategories.performance,
            'performance',
            'bg-gradient-to-r from-yellow-500/20 to-amber-500/20'
          )}

          {/* Overall Notes */}
          <div className="bg-zinc-800/50 rounded-xl p-4">
            <label className="text-sm text-zinc-400 mb-2 block">Overall Notes & Observations</label>
            <textarea
              value={profileData.overall_notes}
              onChange={(e) => setProfileData(prev => ({ ...prev, overall_notes: e.target.value }))}
              placeholder="General observations about the athlete's performance, areas to focus on, etc."
              rows={3}
              className="w-full bg-zinc-800 border border-zinc-700 rounded-lg p-3 text-white resize-none"
            />
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-zinc-700 bg-zinc-900">
          <div className="text-sm text-zinc-400">
            {latestProfile && (
              <span className="flex items-center gap-2">
                <CheckCircle className="h-4 w-4 text-green-400" />
                Last assessment: {latestProfile.assessment_date}
              </span>
            )}
          </div>
          <div className="flex gap-3">
            <Button variant="outline" onClick={onClose} className="border-zinc-700">
              Cancel
            </Button>
            <Button 
              onClick={saveProfile}
              disabled={saving}
              className="bg-gradient-to-r from-[#02b5ff] to-[#ff025f] text-white"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
              Save Profile & Generate Plan
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
